/**!
 *  Leaderboard widget.
 *  Author: Bjorn Tollstrom <bjorn@rodolfo.se>
 */

import React from "react";
import Widget from "../../widget.js";
import "./leaderboard.scss";
import API from "Class/API";
import Auth from "Class/Auth";
import TopListItem from "Components/UI/TopListItem";
import Spinner from "Components/Feedback/Spinner";
import WidgetWrapper from "Components/UI/WidgetWrapper";

class WidgetLeaderboard extends Widget
{
    constructor(props)
    {
        super(props);
        this.Fields = this.SetFields({
            title:
            {
                default: "Leaderboard",
                insert: true,
                label: "Title",
                reset: true,
                type: "text",
                translate: true
            },
            type:
            {
                label: "Type",
                type: "select",
                options: {
                    relative: "Relative",
                    absolute: "Absolute"
                },
                default: 'relative'
            },
            typeNoticeAbsolute:
            {
                displayIf: ["type", "==", "absolute"],
                label: "An absolute leaderboard displays the top places, ie. a typical leaderboard.",
                type: "notice"
            },
            typeNoticeRelative:
            {
                displayIf: ["type", "==", "relative"],
                label: "A relative leaderboard displays the current users position and the positions immidiately above and below.",
                type: "notice"
            },
            community:
            {
                label: "Community",
                placeholder: "Search for community...",
                type: "content",
                types: ["community"]
            },
            communityNotice:
            {
                displayIf: ["community", "==", 0],
                label: "If no community is specified, the leaderboard for the current community will be loaded.",
                type: "notice"
            },
            limit:
            {
                label: "Max. number of people",
                type: "number",
                default: 10
            },
            days:
            {
                label: "Number of days to collect",
                type: "number",
                default: 7
            },
            pointsPerComment:
            {
                label: "Points per comment",
                type: "number",
                default: 2,
                maxValue: 100,
                minValue: 0
            },
            pointsPerEngagement:
            {
                label: "Points per like/share",
                type: "number",
                default: 1,
                maxValue: 100,
                minValue: 0
            }
        }, {
            backgroundColor: "transparent",
            textColor: "black"
        }, true);
        this.Mounted = false;
        this.Name = "Adaptable Leaderboard";
        this.ShowSkull = false;
        this.Translate =
        {
            patience: "Compiling... Please have patience.",
            points: "%u points",
            nocontent: "No leaderboard available"
        };
        this.state =
        {
            error: false,
            leaderboard: [],
            loading: false,
            translations: []
        };
    }

    /**
     * Load leaderboard on mount.
     * @return void
     */

    componentDidMount()
    {
        this.Mounted = true;
        this.Reload();
    }

    /**
     * Reload leaderboard if the community is changed.
     * @return void
     */

    componentDidUpdate(prevProps)
    {
        const {content: ct1} = this.props;
        const {content: ct2} = prevProps;
        const {community: cm1, days: d1, type: t1, limit: l1, pointsPerComment: pc1, pointsPerEngagement: pe1} = ct1 || {};
        const {community: cm2, days: d2, type: t2, limit: l2, pointsPerComment: pc2, pointsPerEngagement: pe2} = ct2 || {};
        const C1 = (cm1 && cm1.length) ? cm1[0][0] : 0;
        const C2 = (cm2 && cm2.length) ? cm2[0][0] : 0;
        if (C1 !== C2 || d1 !== d2 || l1 !== l2 || t1 !== t2 || pc1 !== pc2 || pe1 !== pe2)
        {
            this.Load(C1, d1, l1, t1, pc1, pe1);
        }
    }

    /**
     * Register unmount.
     * @return void
     */

    componentWillUnmount()
    {
        this.Mounted = false;
    }

    Item = ({points, position, id}, index) =>
    {
        return (
            <TopListItem
                className="WidgetLeaderboardItem"
                dead={this.ShowSkull && points < 1}
                description={this.GetTranslation("points").replace("%u", points)}
                id={id}
                index={index}
                key={id}
                highlight={id === Auth.UserId}
                position={position}
                showEmoji={true}
                type="user"
            />
        );
    }

    /**
     * Load the leaderboard.
     * @param integer community - Community id.
     * @param integer days - Number of days to compile.
     * @param integer limit - Number of content items to compile.
     * @param string type - Leaderboard type, either "relative" or "absolute".
     * @param integer pointsPerComment - Points awarded per comment.
     * @param integer pointsPerEngagement - Points awarded per engagement (like/share).
     * @return void
     */

    Load = (community = 0, days = 7, limit = 10, type = "relative", pointsPerComment = 2, pointsPerEngagement = 1) =>
    {
        const {context, contextId} = this.props;
        const Current = context === "community" ? contextId : 0;
        const Community = community || Current;
        this.setState({
            error: false,
            leaderboard: {},
            loading: true
        });
        API.Request("trending/leaderboard",
        {
            community: Community,
            interval: days * 24,
            limit,
            pointsPerComment,
            pointsPerEngagement,
            type
        }, async (response) =>
        {
            if (!this.Mounted)
            {
                return;
            }
            const {leaderboard} = response;
            if (!leaderboard)
            {
                this.setState({error: true, loading: false});
            }
            else {
                this.setState({leaderboard, loading: false});
            }
            this.ScrollToHighlight();
        });
    }

    /**
     * Reload leaderboard.
     * @param integer community - Community id.
     * @return void
     */

    Reload = () =>
    {
        const {content} = this.props;
        const {community: c, days, limit, type, pointsPerComment, pointsPerEngagement} = content || {};
        const Community = (c && c.length) ? c[0][0] : 0;
        this.Load(Community, days, limit, type, pointsPerComment, pointsPerEngagement);
    }

    /**
     * Scroll to the current user (highlighted).
     * @return void
     */

    ScrollToHighlight = () =>
    {
        const Container = this.RefWidget?.querySelector(".ScrollViewContent");
        const HighlightedItem = Container?.querySelector(".Highlight");

        if (!Container || !HighlightedItem)
        {
            return;
        }

        const ContainerHeight = Container.offsetHeight;
        const ItemHeight = HighlightedItem.offsetHeight;
        const Offset =  HighlightedItem.offsetTop;

        Container.scrollTop = Offset - (ContainerHeight / 2) + (ItemHeight / 2);
    }

    render()
    {
        const WidgetContent = this.Content();
        const {active, hover} = this.props;
        const {leaderboard, loading} = this.state;
        const {title} = WidgetContent;
        const CA = this.ClassNames(["WidgetTrending"]);
        if (active)
        {
            CA.push("Active");
        }
        if (hover)
        {
            CA.push("Hover"); 
        }
        const Content = [];
        const Items = [];
        if (leaderboard && leaderboard.length)
        {
            leaderboard.forEach((c, i) =>
            {
                Items.push(this.Item(c, i));
            });
        }
        if (!Items.length && loading)
        {
            Content.push(<Spinner
                className="WidgetSpinner"
                key="spinner"
                message={this.GetTranslation("patience")}
                overlay={true}
            />);
        }
        else if (!Items.length)
        {
            Content.push(<div
                className="WidgetEmpty"
                key="empty"
            >{this.GetTranslation("nocontent")}</div>);
        }
        else
        {
            Content.push(<div
                className="WidgetLeaderboardItems"
                key="items"
            >{Items}</div>);
        }
        return (
            <div className={CA.join(" ")} ref={widget => this.RefWidget = widget} style={this.Style()}>
                {this.Toolbar()}
                <WidgetWrapper
                    {...WidgetContent}
                    className="WidgetLeaderboardWrapper"
                    title={title}
                >
                    {Content}
                </WidgetWrapper>
            </div>
        );
    }
}

export default WidgetLeaderboard;